import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, MailPlus } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { toast } from "sonner";

export function InviteDialog({ open, onOpenChange, onInvited }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post("/orgs/invites", { email, role });
      toast.success(`Invite sent to ${email}`);
      setEmail("");
      onInvited?.(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-[#0a0a0a] text-white sm:max-w-md" data-testid="invite-dialog">
        <DialogHeader>
          <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-xl brand-bg">
            <MailPlus className="h-5 w-5 text-white" />
          </div>
          <DialogTitle className="font-display text-2xl">Invite a teammate</DialogTitle>
          <DialogDescription className="text-white/60">
            They'll get an email link to join this workspace.
          </DialogDescription>
        </DialogHeader>

        <div
          className="flex items-start gap-2 rounded-xl border border-fuchsia-500/25 bg-fuchsia-500/5 p-3 text-xs text-fuchsia-200"
          data-testid="invite-seat-cost-notice"
        >
          <span className="mt-0.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-fuchsia-500/30 text-[10px] text-white">$</span>
          <span>
            <span className="text-white">Inviting this member will add 1 seat (+$9/mo)</span> to your subscription
            on the next billing cycle. No charge today — your seat count grows as members join.
          </span>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-white/50">Email</Label>
            <Input
              required type="email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="border-white/10 bg-black/40 text-white"
              data-testid="invite-email"
              placeholder="teammate@company.com"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-white/50">Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="border-white/10 bg-black/40 text-white" data-testid="invite-role-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-white/10 bg-[#0a0a0a] text-white">
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-white/70 hover:text-white">
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} className="rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="invite-submit">
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send invite
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
